import { useCallback } from 'react'

import { type MultipleOnChange, type SingleOnChange } from '@/components/types'

type Props<TValue, TOption extends Record<string, unknown>> = {
  mode: 'multiple' | undefined
  onChange:
    | SingleOnChange<TValue, TOption>
    | MultipleOnChange<TValue, TOption>
    | undefined
  onExtendedChange?: (
    value: TValue | TValue[] | null,
    option: TOption | TOption[] | null
  ) => void
}

export function useSelectOnChangeHandler<
  TValue,
  TOption extends Record<string, unknown>
>({ mode, onChange, onExtendedChange }: Props<TValue, TOption>) {
  return useCallback(
    (value: TValue | TValue[] | null, option: TOption | TOption[] | null) => {
      if (mode === 'multiple') {
        if (!Array.isArray(value) || !Array.isArray(option)) {
          throw new Error('option or value must be Array')
        }

        ;(onChange as MultipleOnChange<TValue, TOption> | undefined)?.(
          value,
          option
        )
      } else {
        if (Array.isArray(value) || Array.isArray(option)) {
          throw new Error('option or value must be not Array')
        }

        ;(onChange as SingleOnChange<TValue, TOption> | undefined)?.(
          value ?? null,
          option ?? null
        )
      }

      if (onExtendedChange) {
        onExtendedChange(value, option)
      }
    },
    [mode, onChange, onExtendedChange]
  )
}
